import { TURNSTILE_SITE_KEY } from '@/lib/site'

/**
 * Posting a completed tool to the one write path this site has.
 *
 * Shared by all three tools, because the interesting parts are identical and
 * were going to be copied otherwise: the honeypot, the elapsed-time floor, the
 * Turnstile token, and the fact that a failure must leave the visitor holding
 * their own result rather than a blank page.
 *
 * The endpoint is `/api/tool-lead` on this origin — see
 * `src/worker/tools.ts`. Nothing here holds a credential or knows a table name.
 */

export interface ToolLead {
  full_name: string
  email: string
  company: string
  consent: boolean
}

export interface ToolResult {
  /** 0–100. Null for a tool with no single headline number. */
  score: number | null
  /** What the visitor is shown — "Level 1 — Efficiency". */
  verdict: string
  /**
   * The machine key for that verdict — `efficiency`. Sent so the server can
   * look up the facilitator notes for the follow-up task; those notes are
   * deliberately NOT in this bundle, because anything here is readable by the
   * person being assessed.
   */
  band: string
  /** Per-dimension scores, shape is the tool's own. */
  dimensions: Record<string, number>
  /** Every answer, so the follow-up starts from what they said. */
  answers: Record<string, unknown>
}

/** What the caller renders. `sent` is the only state that means "in the CRM". */
export type SubmitState =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'sent' }
  /**
   * `fixable` separates "you left something out" from "we could not save it",
   * which the two callers need to treat differently and did not.
   *
   * Fatih, 2026-09-09, walked the ROI calculator step by step and left the
   * company field blank. The Worker refused with *"Please give us a company."*
   * — a perfectly good sentence — the gate rendered it for a single frame, and
   * then `onDone` moved him to the report, which prints the generic "we could
   * not save this" instead. So a two-second fix was reported as our failure,
   * and the lead was lost.
   *
   * A 4xx is the visitor's to fix and they should stay on the form. A 5xx or a
   * dead network is ours, and the result must not be held hostage to it — see
   * this file's header.
   */
  | { status: 'failed'; message: string; fixable?: boolean; staleChallenge?: boolean }

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string
      getResponse: (id?: string) => string | undefined
      reset: (id?: string) => void
      /** Optional in the type because it is only used to clean up an orphan. */
      remove?: (id?: string) => void
    }
  }
}

export const TURNSTILE_ENABLED = TURNSTILE_SITE_KEY.length > 0

/**
 * Load Turnstile's script once, lazily.
 *
 * Lazily because it is third-party JavaScript on a marketing page: it has no
 * business loading for the many visitors who read the intro and leave, only for
 * the few who reach the form. And once, because two copies race and the second
 * one's `render` finds a widget already there.
 */
let turnstileLoading: Promise<void> | null = null
export function loadTurnstile(): Promise<void> {
  if (!TURNSTILE_ENABLED) return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (turnstileLoading) return turnstileLoading

  turnstileLoading = new Promise((resolve, reject) => {
    // One api.js per page: the newsletter box and the form's inline script
    // load it too, and a second copy races the first. Wait on theirs.
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]',
    )
    if (existing) {
      // An api.js that already FAILED fires no further event, so a loader
      // arriving after the failure would wait forever. Whichever loader saw
      // the error marks the tag `data-dp-failed` (2026-10-02).
      if (existing.hasAttribute('data-dp-failed')) {
        reject(new Error('turnstile failed to load'))
        return
      }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('turnstile failed to load')))
      return
    }
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    // Rejecting rather than hanging: the caller treats a load failure as "no
    // token", and the Worker decides what that means (allowed on a preview,
    // refused in production). A promise that never settles would leave the
    // submit button spinning forever, which is the worst of the options.
    s.onerror = () => {
      s.setAttribute('data-dp-failed', '1')
      reject(new Error('turnstile failed to load'))
    }
    document.head.appendChild(s)
  })
  return turnstileLoading
}

export async function submitTool(
  tool: string,
  locale: string,
  lead: ToolLead,
  result: ToolResult,
  opts: { openedAt: number; honeypot: string; turnstileToken?: string },
): Promise<SubmitState> {
  let res: Response
  try {
    res = await fetch('/api/tool-lead', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        tool,
        locale,
        full_name: lead.full_name,
        email: lead.email,
        company: lead.company,
        consent: lead.consent,
        score: result.score,
        verdict: result.verdict,
        band: result.band,
        dimensions: result.dimensions,
        answers: result.answers,
        turnstile: opts.turnstileToken ?? '',
        // The two cheap checks the Worker makes. `elapsed_ms` is measured from
        // when the tool mounted, not from when the form appeared, because the
        // reading and answering is the part a bot skips.
        elapsed_ms: Date.now() - opts.openedAt,
        website: opts.honeypot,
      }),
    })
  } catch {
    return {
      status: 'failed',
      message: 'We could not reach the server. Your answers are still on screen — try again.',
    }
  }

  if (!res.ok) {
    let message = 'Something went wrong saving that. Your answers are still on screen — try again.'
    /** True only when the Worker says the CHALLENGE is what failed. */
    let staleChallenge = false
    try {
      const body = (await res.json()) as { error?: string; code?: string }
      staleChallenge = body.code === 'challenge'
      // The Worker's messages are written for a visitor to read ("Please tick
      // the box…"), so they are shown as-is. It deliberately never forwards an
      // upstream or Postgres message, so there is nothing here to leak.
      if (body.error) message = body.error
    } catch {
      /* keep the fallback */
    }
    // 4xx means the request was wrong — a missing field, an unticked box, a
    // challenge that did not pass. Recoverable, and the caller keeps them on
    // the form. Anything else is ours.
    return {
      status: 'failed',
      message,
      fixable: res.status >= 400 && res.status < 500,
      staleChallenge,
    }
  }

  return { status: 'sent' }
}
